import { useCallback } from 'react';
import * as Sentry from "@sentry/react";

const useSentryLogger = () => {
  // Breadcrumb logger with predefined categories
  const logBreadcrumb = useCallback((category, message, level = 'info', data = {}) => {
    Sentry.addBreadcrumb({
      category,
      message,
      level,
      data: {
        ...data,
        timestamp: new Date().toISOString()
      }
    });
  }, []);

  // Exception logger with context
  const logException = useCallback((error, context = {}) => {
    Sentry.captureException(error, {
      tags: {
        ...context.tags,
        timestamp: new Date().toISOString()
      },
      extra: context.extra || {},
      level: context.level || 'error'
    });
  }, []);

  // Message logger for warnings and info
  const logMessage = useCallback((message, level = 'info', context = {}) => {
    Sentry.captureMessage(message, {
      level,
      tags: {
        ...context.tags,
        timestamp: new Date().toISOString()
      },
      extra: context.extra || {}
    });
  }, []);

  // WebSocket specific loggers
  const logWebSocketEvent = useCallback((eventType, data = {}) => {
    logBreadcrumb('websocket', `WebSocket ${eventType}`, 'info', {
      eventType,
      ...data
    });
  }, [logBreadcrumb]);

  const logWebSocketError = useCallback((error, context = {}) => {
    logException(error, {
      tags: {
        component: 'websocket',
        action: context.action || 'unknown',
        ...context.tags
      },
      extra: context.extra
    });
  }, [logException]);

  // Video call specific loggers
  const logVideoCallEvent = useCallback((eventType, data = {}) => {
    logBreadcrumb('video_call', `Video call ${eventType}`, 'info', {
      eventType,
      ...data
    });
  }, [logBreadcrumb]);

  const logVideoCallError = useCallback((error, context = {}) => {
    logException(error, {
      tags: {
        component: 'video_call',
        action: context.action || 'unknown',
        ...context.tags
      },
      extra: context.extra
    });
  }, [logException]);

  // Device specific loggers
  const logDeviceEvent = useCallback((eventType, data = {}) => {
    logBreadcrumb('device', `Device ${eventType}`, 'info', {
      eventType,
      ...data
    });
  }, [logBreadcrumb]);

  const logDeviceError = useCallback((error, context = {}) => {
    logException(error, {
      tags: {
        component: 'device',
        action: context.action || 'unknown',
        ...context.tags
      },
      extra: context.extra
    });
  }, [logException]);

  // Screen sharing specific loggers
  const logScreenShareEvent = useCallback((eventType, data = {}) => {
    logBreadcrumb('screen_share', `Screen share ${eventType}`, 'info', {
      eventType,
      ...data
    });
  }, [logBreadcrumb]);

  const logScreenShareError = useCallback((error, context = {}) => {
    logException(error, {
      tags: {
        component: 'screen_share',
        action: context.action || 'unknown',
        ...context.tags
      },
      extra: context.extra
    });
  }, [logException]);

  // Call state loggers
  const logCallStateChange = useCallback((newState, data = {}) => {
    logBreadcrumb('call_state', `Call state changed to ${newState}`, 'info', {
      callState: newState,
      ...data
    });
  }, [logBreadcrumb]);

  // Participant loggers
  const logParticipantEvent = useCallback((eventType, data = {}) => {
    logBreadcrumb('participants', `Participant ${eventType}`, 'info', {
      eventType,
      ...data
    });
  }, [logBreadcrumb]);

  // Interview specific loggers
  const logInterviewEvent = useCallback((eventType, data = {}) => {
    logBreadcrumb('interview', `Interview ${eventType}`, 'info', {
      eventType,
      ...data
    });
  }, [logBreadcrumb]);

  const logInterviewError = useCallback((error, context = {}) => {
    logException(error, {
      tags: {
        component: 'interview',
        action: context.action || 'unknown',
        ...context.tags
      },
      extra: context.extra
    });
  }, [logException]);

  // Permission specific loggers
  const logPermissionEvent = useCallback((eventType, data = {}) => {
    logBreadcrumb('permissions', `Permission ${eventType}`, 'info', {
      eventType,
      ...data
    });
  }, [logBreadcrumb]);

  const logPermissionError = useCallback((error, context = {}) => {
    logException(error, {
      tags: {
        component: 'permissions',
        action: context.action || 'unknown',
        ...context.tags
      },
      extra: context.extra
    });
  }, [logException]);

  // Network specific loggers
  const logNetworkEvent = useCallback((eventType, data = {}) => {
    logBreadcrumb('network', `Network ${eventType}`, 'info', {
      eventType,
      ...data
    });
  }, [logBreadcrumb]);

  const logNetworkError = useCallback((error, context = {}) => {
    logException(error, {
      tags: {
        component: 'network',
        action: context.action || 'unknown',
        ...context.tags
      },
      extra: context.extra
    });
  }, [logException]);

  // Screenshot specific loggers
  const logScreenshotEvent = useCallback((eventType, data = {}) => {
    logBreadcrumb('screenshot', `Screenshot ${eventType}`, 'info', {
      eventType,
      ...data
    });
  }, [logBreadcrumb]);

  const logScreenshotError = useCallback((error, context = {}) => {
    logException(error, {
      tags: {
        component: 'screenshot',
        action: context.action || 'unknown',
        ...context.tags
      },
      extra: context.extra
    });
  }, [logException]);

  // Proctoring specific loggers
  const logProctoringEvent = useCallback((eventType, data = {}) => {
    logBreadcrumb('proctoring', `Proctoring ${eventType}`, 'info', {
      eventType,
      ...data
    });
  }, [logBreadcrumb]);

  const logProctoringError = useCallback((error, context = {}) => {
    logException(error, {
      tags: {
        component: 'proctoring',
        action: context.action || 'unknown',
        ...context.tags
      },
      extra: context.extra
    });
  }, [logException]);

  return {
    // Generic loggers
    logBreadcrumb,
    logException,
    logMessage,
    
    // WebSocket loggers
    logWebSocketEvent,
    logWebSocketError,
    
    // Video call loggers
    logVideoCallEvent,
    logVideoCallError,
    
    // Device loggers
    logDeviceEvent,
    logDeviceError,
    
    // Screen sharing loggers
    logScreenShareEvent,
    logScreenShareError,
    
    // Call state loggers
    logCallStateChange,
    
    // Participant loggers
    logParticipantEvent,
    
    // Interview loggers
    logInterviewEvent,
    logInterviewError,
    
    // Permission loggers
    logPermissionEvent,
    logPermissionError,
    
    // Network loggers
    logNetworkEvent,
    logNetworkError,
    
    // Screenshot loggers
    logScreenshotEvent,
    logScreenshotError,
    
    // Proctoring loggers
    logProctoringEvent,
    logProctoringError
  };
};

export default useSentryLogger; 